// Settings modal. Opens when activityView === "settings"; closes back to the
// explorer. Parameterless: every control is bound directly to store.prefs via
// setPrefs / setTheme, so changes are live and persisted by the store. Controls
// (segmented switches, toggles, steppers) are built from design tokens, no UI
// library, to match the rest of the IDE. Layout follows the editor's own
// settings: a slim section rail on the left, scrollable detail on the right.

import { useEffect, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";
import { Palette, TextCursorInput, PackageCheck, Minus, Plus, X, TriangleAlert, Check } from "lucide-react";
import { useIde } from "../store";
import { THEMES } from "../theme";
import type { ThemeId } from "../theme";
import { CURRENCIES, type Currency } from "../pricing";
import type { EditorMode } from "../store";
import "./overlays.css";

import { NETWORKS, type Network } from "../network";

/** Representative colours per theme for the settings preview swatches. */
const THEME_SWATCH: Record<ThemeId, { bg: string; fg: string; accent: string; ok: string; warn: string; err: string }> = {
  seal: { bg: "#fbeee2", fg: "#574434", accent: "#ee9a76", ok: "#5ea36a", warn: "#c6912f", err: "#d66b54" },
  "tokyo-night": { bg: "#1a1b26", fg: "#c0caf5", accent: "#7aa2f7", ok: "#9ece6a", warn: "#e0af68", err: "#f7768e" },
  bitcoin: { bg: "#14130f", fg: "#ece4d6", accent: "#f7931a", ok: "#b0c94e", warn: "#f5c44d", err: "#f0584f" },
  "catppuccin-mocha": { bg: "#1e1e2e", fg: "#cdd6f4", accent: "#89b4fa", ok: "#a6e3a1", warn: "#f9e2af", err: "#f38ba8" },
  "rose-pine-moon": { bg: "#232136", fg: "#e0def4", accent: "#c4a7e7", ok: "#95b1ab", warn: "#f6c177", err: "#eb6f92" },
  "night-owl": { bg: "#011627", fg: "#d6deeb", accent: "#82aaff", ok: "#addb67", warn: "#ecc48d", err: "#ef5350" },
  dracula: { bg: "#282a36", fg: "#f8f8f2", accent: "#bd93f9", ok: "#50fa7b", warn: "#f1fa8c", err: "#ff5555" },
};

/** The theme picker: a grid of live preview cards, one per registered theme. */
function ThemePicker({ value, onChange }: { value: ThemeId; onChange: (id: ThemeId) => void }): JSX.Element {
  return (
    <div className="set-theme-grid" role="radiogroup" aria-label="Theme">
      {THEMES.map((t) => {
        const sw = THEME_SWATCH[t.id];
        const active = value === t.id;
        return (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={active}
            className={`set-theme-card${active ? " is-active" : ""}`}
            onClick={() => onChange(t.id)}
          >
            <span className="set-theme-swatch" style={{ background: sw.bg }} aria-hidden="true">
              <span className="set-theme-line" style={{ background: sw.fg, width: "58%" }} />
              <span className="set-theme-line" style={{ background: sw.fg, opacity: 0.5, width: "40%" }} />
              <span className="set-theme-dots">
                <i style={{ background: sw.accent }} />
                <i style={{ background: sw.ok }} />
                <i style={{ background: sw.warn }} />
                <i style={{ background: sw.err }} />
              </span>
            </span>
            <span className="set-theme-meta">
              <span className="set-theme-name">{t.label}</span>
              {active && <Check size={14} strokeWidth={2.25} className="set-theme-check" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** The sections, in order. Drives both the left rail and the scroll-spy. */
const SECTIONS = [
  { id: "appearance", label: "Appearance", icon: <Palette size={16} strokeWidth={1.75} /> },
  { id: "editor", label: "Editor", icon: <TextCursorInput size={16} strokeWidth={1.75} /> },
  { id: "compilation", label: "Compilation", icon: <PackageCheck size={16} strokeWidth={1.75} /> },
] as const;
type SectionId = (typeof SECTIONS)[number]["id"];

/** A labelled toggle switch bound to a boolean. */
function Toggle({
  id,
  checked,
  onChange,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}): JSX.Element {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      className={`set-toggle${checked ? " is-on" : ""}`}
      onClick={() => onChange(!checked)}
    >
      <span className="set-toggle-knob" />
    </button>
  );
}

/** A segmented (radio-group) control. */
function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string; sub?: string }[];
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div className="set-segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={`set-seg${o.value === value ? " is-active" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.sub && <span className="set-seg-sub">{o.sub}</span>}
        </button>
      ))}
    </div>
  );
}

/** A single setting. The label sits on top; compact controls (toggle, stepper)
 *  ride inline on the right of the label, wide controls (segmented, theme grid)
 *  stack below via `stack`. The help text is ALWAYS full width beneath the
 *  label, so it never collapses into a narrow sliver beside a wide control. */
function Field({
  label,
  help,
  htmlFor,
  control,
  stack = false,
}: {
  label: string;
  help: ReactNode;
  htmlFor?: string;
  control: ReactNode;
  stack?: boolean;
}): JSX.Element {
  const labelEl = htmlFor ? (
    <label className="set-item-label" htmlFor={htmlFor}>
      {label}
    </label>
  ) : (
    <span className="set-item-label">{label}</span>
  );
  return (
    <div className={`set-item${stack ? " set-item--stack" : ""}`}>
      <div className="set-item-head">
        {labelEl}
        {!stack && <div className="set-item-control">{control}</div>}
      </div>
      <p className="set-item-help">{help}</p>
      {stack && <div className="set-item-control set-item-control--block">{control}</div>}
    </div>
  );
}

export function SettingsModal(): JSX.Element | null {
  const activityView = useIde((s) => s.activityView);
  const setActivityView = useIde((s) => s.setActivityView);
  const prefs = useIde((s) => s.prefs);
  const setPrefs = useIde((s) => s.setPrefs);
  const setTheme = useIde((s) => s.setTheme);

  const open = activityView === "settings";
  const closeRef = useRef<HTMLButtonElement>(null);
  const [active, setActive] = useState<SectionId>("appearance");

  const close = () => setActivityView("explorer");

  // Escape closes; focus the close button on open for keyboard reachability.
  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    setActive("appearance");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const clampFont = (n: number): number => Math.max(10, Math.min(20, Math.round(n)));

  return (
    <div
      className="set-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="set-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="set-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="set-header">
          <div className="set-header-text">
            <h2 className="set-title" id="set-title">
              Settings
            </h2>
            <p className="set-subtitle">Preferences are saved to this browser.</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="set-close"
            aria-label="Close settings"
            title="Close (Esc)"
            onClick={close}
          >
            <X size={16} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </header>

        <div className="set-main">
          <nav className="set-nav" aria-label="Settings sections">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`set-nav-item${active === s.id ? " is-active" : ""}`}
                aria-current={active === s.id ? "true" : undefined}
                onClick={() => setActive(s.id)}
              >
                <span className="set-nav-icon">{s.icon}</span>
                <span>{s.label}</span>
              </button>
            ))}
          </nav>

          <div className="set-body">
            {active === "appearance" && (
            <section className="set-section">
              <h3 className="set-section-title">Appearance</h3>

              <Field
                stack
                label="Theme"
                help="The colour palette for the whole IDE, chrome and editor. Matched to each reference by exact hex."
                control={<ThemePicker value={prefs.theme} onChange={(id) => setTheme(id)} />}
              />

              <Field
                label="Font size"
                help="Editor font size, in pixels (10-20)."
                htmlFor="set-fontsize"
                control={
                  <div className="set-stepper">
                    <button
                      type="button"
                      className="set-step-btn"
                      aria-label="Decrease font size"
                      disabled={prefs.fontSize <= 10}
                      onClick={() => setPrefs({ fontSize: clampFont(prefs.fontSize - 1) })}
                    >
                      <Minus size={14} strokeWidth={2} aria-hidden="true" />
                    </button>
                    <input
                      id="set-fontsize"
                      className="set-step-input"
                      type="number"
                      min={10}
                      max={20}
                      value={prefs.fontSize}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        if (Number.isFinite(v)) setPrefs({ fontSize: clampFont(v) });
                      }}
                    />
                    <button
                      type="button"
                      className="set-step-btn"
                      aria-label="Increase font size"
                      disabled={prefs.fontSize >= 20}
                      onClick={() => setPrefs({ fontSize: clampFont(prefs.fontSize + 1) })}
                    >
                      <Plus size={14} strokeWidth={2} aria-hidden="true" />
                    </button>
                  </div>
                }
              />

              <Field
                label="Minimap"
                help="Show the code overview map on the right of the editor."
                htmlFor="set-minimap"
                control={
                  <Toggle
                    id="set-minimap"
                    checked={prefs.minimap}
                    onChange={(v) => setPrefs({ minimap: v })}
                  />
                }
              />

              <Field
                label="Word wrap"
                help="Wrap long lines instead of scrolling horizontally."
                htmlFor="set-wordwrap"
                control={
                  <Toggle
                    id="set-wordwrap"
                    checked={prefs.wordWrap}
                    onChange={(v) => setPrefs({ wordWrap: v })}
                  />
                }
              />
            </section>
            )}

            {active === "editor" && (
            <section className="set-section">
              <h3 className="set-section-title">Editor</h3>

              <Field
                label="Auto-lint on type"
                help="Continuously check the contract as you edit, surfacing diagnostics inline."
                htmlFor="set-autolint"
                control={
                  <Toggle
                    id="set-autolint"
                    checked={prefs.autoLint}
                    onChange={(v) => setPrefs({ autoLint: v })}
                  />
                }
              />

              <Field
                stack
                label="Keys"
                help="Modal vim keys in the editor, with the mode and the : command line shown in the status bar. Loaded on demand, so it costs nothing until you turn it on."
                control={
                  <Segmented<EditorMode>
                    label="Keys"
                    value={prefs.editorMode}
                    options={[
                      { value: "normal", label: "Default" },
                      { value: "vim", label: "Vim" },
                    ]}
                    onChange={(m) => setPrefs({ editorMode: m })}
                  />
                }
              />

              <Field
                stack
                label="Currency"
                help="Fiat for the per-leaf spend cost shown in the Script tab. The BTC price and fee rate are fetched live from mempool.space."
                control={
                  <Segmented<Currency>
                    label="Currency"
                    value={prefs.currency}
                    options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                    onChange={(c) => setPrefs({ currency: c })}
                  />
                }
              />
            </section>
            )}

            {active === "compilation" && (
            <section className="set-section">
              <h3 className="set-section-title">Compilation</h3>

              <Field
                stack
                label="Network"
                help="The Bitcoin network the funding address is encoded for. Test networks only: this build of the compiler cannot produce a mainnet address."
                control={
                  <Segmented<Network>
                    label="Network"
                    value={prefs.network}
                    options={NETWORKS.map((n) => ({
                      value: n.id,
                      label: n.label,
                      sub: n.prefix,
                    }))}
                    onChange={(n) => setPrefs({ network: n })}
                  />
                }
              />

              <Field
                label="Allow unproven"
                help="Compile even when the certifier cannot prove every spend path matches its predicate."
                htmlFor="set-allowunproven"
                control={
                  <Toggle
                    id="set-allowunproven"
                    checked={prefs.allowUnproven}
                    onChange={(v) => setPrefs({ allowUnproven: v })}
                  />
                }
              />
              {prefs.allowUnproven && (
                <div className="set-warning" role="alert">
                  <TriangleAlert
                    size={15}
                    strokeWidth={1.75}
                    className="set-warning-icon"
                    aria-hidden="true"
                  />
                  <span>
                    This can emit a funding address for a contract that has{" "}
                    <strong>not been proven</strong> to match its predicate. Funds sent to an
                    unproven address may be unspendable or spendable by an unintended party. Use only
                    when you understand the risk.
                  </span>
                </div>
              )}
            </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
